/**
 * Helpers de transfert de fichiers — protocole FB binaire (cf. spec).
 *
 * Extrait de panels/files.tsx pour réutilisation dans le panneau chat
 * (bouton trombone 📎). Toutes les fonctions sont pures ou ne dépendent
 * que de l'API WsApi standard (send/sendBinary/bufferedAmount).
 *
 * Protocole :
 *   - Cadre binaire `FB` (magic 0x46 0x42) via ws.sendBinary — pas de base64.
 *   - Contrôle JSON `file:*` (offer/resume/ack/done/complete/cancel/error).
 *   - Reprise par offset, sha256 streaming (WebCrypto), fenêtre 4 MiB.
 */
import { useWs } from "@/shared/ws";
import type { WsIncoming } from "@/shared/types";

// Type de l'API WS (ReturnType car WsApi n'est pas exporté publiquement).
type WsApi = ReturnType<typeof useWs>;
export const FB_MAGIC0 = 0x46; // 'F'
export const FB_MAGIC1 = 0x42; // 'B'
export const FB_VERSION = 1;
export const FB_FLAG_LAST = 0x01;
export const FB_HEADER_FIXED = 18; // octets fixes avant fileId

export const CHUNK_SIZE = 256 * 1024; // 256 KiB (spec §1)
export const WINDOW_BYTES = 4 * 1024 * 1024; // fenêtre flow-control 4 MiB (spec §3)
export const WS_BUFFER_HIGH_WATER = 4 * 1024 * 1024; // backpressure bufferedAmount
export const ACK_INTERVAL_BYTES = 1024 * 1024; // ack récepteur tous les ~1 MiB (spec §4)
export const FILE_MAX_BYTES = 100 * 1024 * 1024; // quota 100 MiB (spec §9)

// MIME allowlist + sanitize.
const ALLOWED_MIME_REGEX =
  /^(text\/|image\/|application\/(pdf|json|zip|x-tar|gzip|octet-stream|vnd\.openxmlformats)|video\/|audio\/)/i;

export function isAllowedMime(mime: string): boolean {
  return ALLOWED_MIME_REGEX.test(mime || "application/octet-stream");
}

export function sanitizeFilename(name: string): string | null {
  if (!name) return "fichier";
  if (/[\\/\x00-\x1f]/.test(name)) return null;
  return name.slice(0, 255);
}

export function safeUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  return [...b]
    .map((x, i) => (i === 4 || i === 6 || i === 8 || i === 10 ? "-" : "") + x.toString(16).padStart(2, "0"))
    .join("");
}

export function fmtBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} Mo`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} Go`;
}

// ── Construction du cadre FB (spec §1, big-endian) ──────────────────────────
export function buildFbFrame(fileId: string, offset: number, payload: Uint8Array, last: boolean): ArrayBuffer {
  const idBytes = new TextEncoder().encode(fileId);
  const buf = new ArrayBuffer(FB_HEADER_FIXED + idBytes.length + payload.length);
  const view = new DataView(buf);
  const u8 = new Uint8Array(buf);
  view.setUint8(0, FB_MAGIC0);
  view.setUint8(1, FB_MAGIC1);
  view.setUint8(2, FB_VERSION);
  view.setUint8(3, last ? FB_FLAG_LAST : 0);
  view.setUint16(4, idBytes.length, false); // BE
  view.setBigUint64(6, BigInt(offset), false); // BE
  view.setUint32(14, payload.length, false); // BE
  u8.set(idBytes, FB_HEADER_FIXED);
  u8.set(payload, FB_HEADER_FIXED + idBytes.length);
  return buf;
}

export interface ParsedFb {
  fileId: string;
  offset: number;
  payload: Uint8Array;
  last: boolean;
}

export function parseFbFrame(data: ArrayBuffer): ParsedFb | null {
  if (data.byteLength < FB_HEADER_FIXED) return null;
  const view = new DataView(data);
  if (view.getUint8(0) !== FB_MAGIC0 || view.getUint8(1) !== FB_MAGIC1) return null;
  const idLen = view.getUint16(4, false);
  const offset = Number(view.getBigUint64(6, false));
  const payloadLen = view.getUint32(14, false);
  if (FB_HEADER_FIXED + idLen + payloadLen > data.byteLength) return null;
  const fileId = new TextDecoder().decode(new Uint8Array(data, FB_HEADER_FIXED, idLen));
  const payload = new Uint8Array(data, FB_HEADER_FIXED + idLen, payloadLen);
  return { fileId, offset, payload, last: (view.getUint8(3) & FB_FLAG_LAST) !== 0 };
}

// ── SHA-256 streaming (WebCrypto) ──────────────────────────────────────────
export async function computeSha256Streaming(file: Blob): Promise<string> {
  const reader = file.stream().getReader();
  const digest = await crypto.subtle.digest.bind(crypto.subtle);
  // Pas de streaming digest natif en WebCrypto ; on charge en flux chunké et
  // combine. Pour les fichiers < 100 MiB (quota), on peut bufferiser.
  const buf = await file.arrayBuffer();
  const hash = await digest("SHA-256", buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ── Orchestrateur d'upload (offer → FB* → done) ────────────────────────────
export interface UploadProgress {
  fileName: string;
  bytesTransferred: number;
  bytesTotal: number;
  percent: number;
  status: "offering" | "uploading" | "done" | "error";
  error?: string;
}

export interface UploadOptions {
  ws: WsApi;
  file: File | Blob;
  fileName?: string;
  mime?: string;
  /** Dossier de destination host (optionnel — sinon fallback downloads). */
  destDir?: string;
  onProgress?: (p: UploadProgress) => void;
}

/**
 * Upload complet opérateur→host : SHA-256 → file:offer → cadres FB → file:done.
 * Flow-control via bufferedAmount + fenêtre 4 MiB non-ackée.
 * Retourne une fonction `cancel()` pour interrompre l'envoi.
 */
export function uploadFile(opts: UploadOptions): { promise: Promise<void>; cancel: () => void } {
  const { ws, file, destDir, onProgress } = opts;
  const fileName = opts.fileName ?? (file instanceof File ? file.name : "fichier");
  const mime = opts.mime ?? (file instanceof File ? file.type : "application/octet-stream");
  const size = file.size;
  const fileId = safeUuid();

  let cancelled = false;
  let ackedOffset = 0;

  const report = (status: UploadProgress["status"], bytesTransferred: number, error?: string) => {
    onProgress?.({
      fileName,
      bytesTransferred,
      bytesTotal: size,
      percent: size > 0 ? Math.round((bytesTransferred / size) * 100) : 0,
      status,
      error,
    });
  };

  const promise = (async () => {
    if (size > FILE_MAX_BYTES) {
      report("error", 0, `Fichier > ${FILE_MAX_BYTES / 1024 / 1024} Mo`);
      throw new Error(`Fichier trop volumineux (${size} > ${FILE_MAX_BYTES})`);
    }

    const safeName = sanitizeFilename(fileName);
    if (!safeName) {
      report("error", 0, "Nom de fichier invalide");
      throw new Error("Nom de fichier invalide");
    }

    // SHA-256 pour l'intégrité.
    const sha256 = await computeSha256Streaming(file);

    // Annonce de capacité si pas déjà fait.
    ws.send({ type: "operator:capabilities", capabilities: { fileBinary: true } });

    // offer
    report("offering", 0);
    ws.send({
      type: "file:offer",
      fileId,
      name: safeName,
      size,
      mime: isAllowedMime(mime) ? mime : "application/octet-stream",
      sha256,
      chunkSize: CHUNK_SIZE,
      ...(destDir ? { destDir } : {}),
    });

    // Attendre file:resume{ackedOffset} (reprise) — handler ws.on.
    const resumeP = new Promise<number>((resolve) => {
      const off = ws.on(
        (m: WsIncoming) => m.type === "file:resume",
        (m: WsIncoming) => {
          const r = m as { type: "file:resume"; fileId?: string; ackedOffset?: number };
          if (r.fileId !== fileId) return;
          off();
          resolve(r.ackedOffset ?? 0);
        },
      );
      // Timeout de sécurité : si pas de resume en 30s, on part de 0.
      setTimeout(() => {
        off();
        resolve(0);
      }, 30_000);
    });
    ackedOffset = await resumeP;

    // Stream des cadres FB par chunks.
    report("uploading", ackedOffset);
    let offset = ackedOffset;
    const reader = file.stream().getReader();

    // Seek jusqu'à l'offset de reprise (si > 0).
    if (offset > 0) {
      let skipped = 0;
      while (skipped < offset) {
        const { done, value } = await reader.read();
        if (done) break;
        skipped += value.length;
        if (skipped >= offset) {
          // Le dernier chunk lu dépasse offset — on l'utilise partiellement.
          if (skipped > offset && value) {
            const remaining = value.subarray(value.length - (skipped - offset));
            if (remaining.length > 0) {
              offset += await sendChunk(remaining, offset);
            }
          }
          break;
        }
      }
    }

    // Lecture + envoi en flux.
    while (!cancelled) {
      // Backpressure : attendre si bufferedAmount trop élevé.
      while (ws.bufferedAmount() > WS_BUFFER_HIGH_WATER) {
        if (cancelled) break;
        await new Promise((r) => setTimeout(r, 50));
      }
      if (cancelled) break;

      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;

      offset += await sendChunk(value, offset);
      report("uploading", offset);
    }
    await reader.cancel();

    if (cancelled) {
      ws.send({ type: "file:cancel", fileId });
      return;
    }

    // done
    ws.send({ type: "file:done", fileId, sha256 });
    report("done", size);
  })();

  // Helper : découpe un Uint8Array en chunks de CHUNK_SIZE, envoie via ws.sendBinary.
  async function sendChunk(data: Uint8Array, baseOffset: number): Promise<number> {
    let sent = 0;
    for (let i = 0; i < data.length; i += CHUNK_SIZE) {
      if (cancelled) break;
      const chunk = data.subarray(i, i + CHUNK_SIZE);
      const isLast = baseOffset + i + chunk.length >= size;
      const frame = buildFbFrame(fileId, baseOffset + i, chunk, isLast);
      ws.sendBinary(frame);
      sent += chunk.length;
      // Backpressure inter-chunk.
      while (ws.bufferedAmount() > WS_BUFFER_HIGH_WATER) {
        if (cancelled) break;
        await new Promise((r) => setTimeout(r, 20));
      }
    }
    return sent;
  }

  return {
    promise,
    cancel: () => {
      cancelled = true;
    },
  };
}
